import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  HStack,
  IconButton,
  SimpleGrid,
  VStack,
} from '@chakra-ui/react';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Icon from 'components/Icon';
import { ProtectedElement } from 'components/ProtectedElement';
import Text from 'components/Text';
import { useMediaQueries } from 'layout/MediaQueriesProvider';
import { ERoute } from 'types/frontend';

import { CVTemplate } from '../types';

const MAX_PDF_SIZE_MB = 5;
const MAX_PDF_SIZE_BYTES = MAX_PDF_SIZE_MB * 1024 * 1024;

const TEMPLATE_OPTIONS: { value: CVTemplate; label: string; description: string }[] = [
  {
    value: 'classic',
    label: 'Classic',
    description: 'Single column, traditional layout.',
  },
  {
    value: 'modern',
    label: 'Modern',
    description: 'Two-column layout with sidebar.',
  },
  {
    value: 'creative',
    label: 'Creative',
    description: 'Accent blocks and visual flair.',
  },
];

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const PDFUploadTab = () => {
  const navigate = useNavigate();
  const { toastError } = useToastWrapper();
  const { isMobile, isDesktopNavigation } = useMediaQueries();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<CVTemplate>('classic');
  const [isDragging, setIsDragging] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((file: File) => {
    if (file.type !== 'application/pdf') {
      toastError('Only PDF files are accepted.');

      return;
    }

    if (file.size > MAX_PDF_SIZE_BYTES) {
      toastError(`File is too large. Maximum size is ${MAX_PDF_SIZE_MB}MB.`);

      return;
    }

    setSelectedFile(file);
  }, [toastError]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];

      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleImport = useCallback(async () => {
    if (!selectedFile) return;

    setIsReading(true);

    const reader = new FileReader();

    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];

      // Navigate to the streaming import view with PDF data
      navigate(`${ERoute.CVBuilder}${ERoute.ImportingCV}`, {
        state: { mode: 'import', pdfBase64: base64, template: selectedTemplate },
      });
    };

    reader.onerror = () => {
      setIsReading(false);
      toastError('Failed to read the PDF file.');
    };

    reader.readAsDataURL(selectedFile);
  }, [selectedFile, selectedTemplate, navigate, toastError]);

  return (
    <VStack spacing={8} alignItems="stretch" flex={1}>
      {/* Drop zone */}
      <Box
        backgroundColor="whiteAlpha.50"
        borderRadius="xl"
        border="2px dashed"
        borderColor={isDragging ? 'purple.400' : selectedFile ? 'green.400' : 'whiteAlpha.200'}
        p={8}
        height="180px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        cursor={selectedFile ? 'default' : 'pointer'}
        onClick={() => !selectedFile && fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        transition="all 0.2s"
        _hover={{ borderColor: selectedFile ? 'green.400' : 'whiteAlpha.400' }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];

            if (file) {
              handleFileSelect(file);
            }

            e.target.value = '';
          }}
        />
        {selectedFile ? (
          <HStack spacing={3}>
            <Icon type="FileStorage" size={20} />
            <VStack spacing={0} alignItems="flex-start">
              <Text fontSize="small" fontWeight="semibold">
                {selectedFile.name}
              </Text>
              <Text fontSize="micro" opacity="tertiary">
                {formatFileSize(selectedFile.size)}
              </Text>
            </VStack>
            <IconButton
              aria-label="Remove file"
              icon={<CloseIcon boxSize={2} />}
              size="xs"
              variant="ghost"
              onClick={() => setSelectedFile(null)}
            />
          </HStack>
        ) : (
          <VStack spacing={3}>
            <Icon type="UploadImage" size={32} />
            <VStack spacing={1}>
              <Text fontSize="medium" fontWeight="semibold">
                Drop your CV here
              </Text>
              <Text fontSize="small" opacity="secondary">
                or{' '}
                <Box
                  as="button"
                  color="purple.300"
                  textDecoration="underline"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  browse files
                </Box>
              </Text>
              <Text fontSize="micro" opacity="tertiary">
                PDF only, up to {MAX_PDF_SIZE_MB}MB
              </Text>
            </VStack>
          </VStack>
        )}
      </Box>

      {/* Data Privacy Note */}
      <HStack
        spacing={2}
        px={4}
        py={3}
        borderRadius="lg"
        backgroundColor="whiteAlpha.50"
        border="1px solid"
        borderColor="whiteAlpha.100"
      >
        <Icon type="Lock" size={14} />
        <Text fontSize="micro" opacity="secondary">
          Your PDF is processed securely and never stored on our servers. Only the extracted text is
          saved to populate your CV.
        </Text>
      </HStack>

      {/* Template Selector */}
      <Box
        backgroundColor="whiteAlpha.50"
        borderRadius="xl"
        border="1px solid"
        borderColor="whiteAlpha.100"
        p={6}
      >
        <Text fontSize="large" fontWeight="semibold" mb={4}>
          Choose a template
        </Text>
        <SimpleGrid columns={isDesktopNavigation ? 3 : 1} gap={4}>
          {TEMPLATE_OPTIONS.map((template) => (
            <Box
              key={template.value}
              p={4}
              borderRadius="lg"
              border="2px solid"
              borderColor={selectedTemplate === template.value ? 'purple.400' : 'whiteAlpha.200'}
              backgroundColor={
                selectedTemplate === template.value ? 'whiteAlpha.100' : 'transparent'
              }
              cursor="pointer"
              transition="all 0.2s"
              _hover={{ borderColor: 'whiteAlpha.400' }}
              onClick={() => setSelectedTemplate(template.value)}
            >
              {/* Mini template preview */}
              <Box
                backgroundColor="white"
                borderRadius="md"
                p={3}
                mb={3}
                height={isMobile ? '80px' : '100px'}
                overflow="hidden"
              >
                {template.value === 'classic' && (
                  <>
                    <Box height="3px" width="50%" backgroundColor="gray.400" borderRadius="full" mb={2} mx="auto" />
                    <Box height="2px" width="30%" backgroundColor="gray.300" borderRadius="full" mb={3} mx="auto" />
                    <Box height="2px" width="80%" backgroundColor="gray.200" borderRadius="full" mb={1} />
                    <Box height="2px" width="70%" backgroundColor="gray.200" borderRadius="full" mb={1} />
                    <Box height="2px" width="75%" backgroundColor="gray.200" borderRadius="full" />
                  </>
                )}
                {template.value === 'modern' && (
                  <HStack spacing={2} height="100%">
                    <Box width="35%" height="100%" backgroundColor="gray.200" borderRadius="sm" p={1}>
                      <Box height="2px" width="80%" backgroundColor="gray.400" borderRadius="full" mb={1} />
                      <Box height="1px" width="60%" backgroundColor="gray.300" borderRadius="full" mb={1} />
                      <Box height="1px" width="70%" backgroundColor="gray.300" borderRadius="full" />
                    </Box>
                    <Box flex={1}>
                      <Box height="3px" width="60%" backgroundColor="gray.400" borderRadius="full" mb={2} />
                      <Box height="2px" width="90%" backgroundColor="gray.200" borderRadius="full" mb={1} />
                      <Box height="2px" width="80%" backgroundColor="gray.200" borderRadius="full" mb={1} />
                      <Box height="2px" width="85%" backgroundColor="gray.200" borderRadius="full" />
                    </Box>
                  </HStack>
                )}
                {template.value === 'creative' && (
                  <>
                    <Box height="4px" width="100%" backgroundColor="purple.200" borderRadius="full" mb={2} />
                    <Box height="3px" width="40%" backgroundColor="gray.400" borderRadius="full" mb={2} />
                    <Box height="3px" width="100%" backgroundColor="purple.100" borderRadius="full" mb={2} />
                    <Box height="2px" width="80%" backgroundColor="gray.200" borderRadius="full" mb={1} />
                    <Box height="2px" width="70%" backgroundColor="gray.200" borderRadius="full" />
                  </>
                )}
              </Box>
              <Text fontSize="medium" fontWeight="semibold">
                {template.label}
              </Text>
              <Text fontSize="small" opacity="secondary">
                {template.description}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
        <Text fontSize="micro" opacity="tertiary" mt={3}>
          You can change the template anytime later for free.
        </Text>
      </Box>

      {/* Spacer pushes bottom bar down */}
      <Box flex={1} />

      {/* Bottom action bar — docked to bottom */}
      <Box
        position="sticky"
        bottom="-60px"
        mb="-60px"
        py={3}
        display="flex"
        justifyContent="center"
        borderTop="1px solid"
        borderColor="whiteAlpha.100"
        backgroundColor="gray.900"
        zIndex={10}
      >
        <ProtectedElement
          scheme="gradient"
          size="lg"
          isDisabled={!selectedFile || isReading}
          isLoading={isReading}
          onClick={handleImport}
          requiredTokens={3}
        >
          Import CV
        </ProtectedElement>
      </Box>
    </VStack>
  );
};

export default PDFUploadTab;
